import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { PageHeader } from "@/app/PageHeader";
import { useAsync } from "@/lib/useAsync";
import { IconCpu } from "@/lib/icons";

import { listWorkspaceDevices } from "../api";
import { DeviceList } from "../components/DeviceList";
import { EmptyState } from "../components/EmptyState";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";

/**
 * Lista de devices associados a um workspace. Rota legada
 * `/workspaces/:workspaceId/devices` — mantida apenas como atalho
 * técnico até a fase de banco.
 */
export function WorkspaceDevicesPage() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const decodedWorkspaceId = decodeURIComponent(workspaceId);
  const [filter, setFilter] = useState("");

  const { state, reload } = useAsync(
    `workspaces/${decodedWorkspaceId}/devices`,
    (signal) => listWorkspaceDevices(decodedWorkspaceId, signal),
  );

  const filtered = useMemo(() => {
    if (!state.data) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return state.data;
    return state.data.filter((device) => {
      const hay = `${device.mqttId ?? ""} ${device.mac ?? ""} ${device.status ?? ""} ${
        device.serialNumber ?? ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [state.data, filter]);

  const total = state.data?.length ?? 0;

  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Dashboard", to: "/workflows" },
          { label: "Workspace" },
          { label: decodedWorkspaceId || "(sem workspace)" },
        ]}
        title={decodedWorkspaceId || "Workspace"}
        meta={
          <>
            <span className="item">
              <IconCpu size={12} /> Devices <b>{total}</b>
            </span>
          </>
        }
      />
      <div className="content">
        <div className="section-h">
          <h2>
            Devices associados <span className="num">{total}</span>
          </h2>
          <div className="right">
            <input
              type="search"
              placeholder="Filtrar por mqttId, MAC, status..."
              className="input"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              aria-label="Filtrar devices do workspace"
              style={{ minWidth: 260 }}
            />
          </div>
        </div>

        {state.status === "loading" && !state.data ? (
          <LoadingState label="Buscando devices do workspace..." />
        ) : null}

        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={reload} />
        ) : null}

        {state.data && filtered.length === 0 ? (
          <EmptyState
            title={filter ? "Nenhum device com esse filtro" : "Nenhum device neste workspace"}
            message={
              filter
                ? "Tente outro termo ou limpe o filtro."
                : "Associe um device pelo painel de claim para ele aparecer aqui."
            }
          />
        ) : null}

        {filtered.length > 0 ? <DeviceList devices={filtered} detailsSearch={`?workspaceId=${encodeURIComponent(decodedWorkspaceId)}`} /> : null}
      </div>
    </>
  );
}
